import type { Ref } from 'vue'
import type { AppTextareaEvent, InputPrimaryMode, NativeSelectOption } from '@shared/ui/fields'

import { TaskStatus } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineStore } from './store'
import { useDisciplineTasksStore } from './tasks/store'
import { useTutorTaskStoreApi } from '../api/tutor-tasks/store.hook'
import { useDisciplineTasksRefresh } from './discipline-tasks-refresh.hook'

interface ModalDisciplineTaskCreateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsSaving: Ref<boolean>
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refStatus: Ref<TaskStatus>
  refTutorId: Ref<number | null>
  refBookingId: Ref<string | number>
  refBookingIdMode: Ref<InputPrimaryMode>
  setBookingId: (value: string | number) => void
  refDescription: Ref<string>
  setStatus: (option: NativeSelectOption) => void
  setDescription: (event: AppTextareaEvent) => void
  closeModal: () => void
  createTask: () => Promise<void>
}

export const useModalDisciplineTaskCreate = (): ModalDisciplineTaskCreateReturn => {
  const toast = useToast()
  const disciplineStore = useDisciplineStore()
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const { writeAndRefresh } = useDisciplineTasksRefresh()

  const refIsSaving = ref(false)
  const refStatus = ref<TaskStatus>(TaskStatus.Planned)
  const refTutorId = ref<number | null>(null)
  const refBookingId = ref<string | number>('')
  const refBookingIdMode = ref<InputPrimaryMode>('normal')
  const refDescription = ref('')

  const refIsOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.Create)

  const refStatusOptions = computed<NativeSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.tutor_task.status ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refStatus.value,
    })))

  const setStatus = (option: NativeSelectOption): void => {
    refStatus.value = option.value as TaskStatus
  }

  const setDescription = (event: AppTextareaEvent): void => {
    refDescription.value = event.value
  }

  const closeModal = (): void => {
    if (refIsSaving.value) return
    store.closeModal()
  }

  /** The legacy form started on the first status and a tutor; the discipline's own tutor is the useful default. */
  const resetForm = (): void => {
    const statuses = Object.keys(listsStore.getLists?.tutor_task.status ?? {})
    refStatus.value = (statuses[0] as TaskStatus | undefined) ?? TaskStatus.Planned
    refTutorId.value = disciplineStore.getDiscipline?.tutor_id ?? null
    refBookingId.value = ''
    refBookingIdMode.value = 'normal'
    refDescription.value = ''
  }

  const createTask = async (): Promise<void> => {
    if (refIsSaving.value) return

    const bookingId = Number(refBookingId.value)
    const isBookingValid = Number.isInteger(bookingId) && bookingId > 0
    refBookingIdMode.value = isBookingValid ? 'normal' : 'error'

    if (!isBookingValid) {
      toast.warning('Booking ID field is required.')
      return
    }

    if (!refTutorId.value) {
      toast.warning('Tutor field is required.')
      return
    }

    const tutorId = refTutorId.value

    await writeAndRefresh(refIsSaving, () => useTutorTaskStoreApi({
      payload: {
        status: refStatus.value,
        tutor_id: tutorId,
        booking_id: bookingId,
        description: refDescription.value.trim() || null,
      },
    }))
  }

  watch(refIsOpen, (isOpen) => {
    if (isOpen) resetForm()
  })

  const setBookingId = (value: string | number): void => {
    refBookingId.value = value
    refBookingIdMode.value = 'normal'
  }

  return {
    refIsOpen,
    refIsSaving,
    refStatusOptions,
    refStatus,
    refTutorId,
    refBookingId,
    refBookingIdMode,
    setBookingId,
    refDescription,
    setStatus,
    setDescription,
    closeModal,
    createTask,
  }
}
